<template>
  <AdvisorDialog class="relative">
    <h2 class="text-center font-bold mb-1 text-23">
      {{ heading }}
    </h2>
    <!-- comment in code for retrigger -->
    <form @submit.prevent="handleSubmit">
      <ReasonDropdown
        :invalid="isInvalid"
        :dropdown-label="dropdownLabel"
        placeholder="Choose your reason"
        class="w-full text-center"
        :show-reset="false"
        :searchable="false"
        data-qa="bambee-interest-dropdown-select"
        input-size="normal"
      />
      <BaseButton
        :id="buttonId"
        variant="secondary"
        post-icon="arrow-right"
        size="large"
        class="my-4"
        block
        type="submit"
      >
        {{ buttonText }}
      </BaseButton>
    </form>
    <div class="flex justify-center">
      <a v-if="planLinkHref" :href="planLinkHref" class="underline">
        <TypeBody tag="span" class="italic">{{ planLinkLabel }}</TypeBody>
      </a>
      <TypeBody v-else class="italic">{{ planLinkLabel }}</TypeBody>
    </div>
  </AdvisorDialog>
</template>

<script>
import Analytics from '@/services/Analytics';
import { BaseButton, TypeBody } from '@bambeehr/pollen';
import { computed, ref, useRoute } from '@nuxtjs/composition-api';

import AdvisorDialog from '@/components/AdvisorDialog';
import useRadioPromo from '@/hooks/useRadioPromo';
import useRegistration, {
  RegistrationStateNames,
} from '@/modules/SelfServiceRegistration/hooks/useRegistration';
import selfCheckout from '@/constants/SelfCheckout';

import SelfServicePages from '@/modules/SelfServiceRegistration/constants/SelfServicePages';
import useMatchMedia from '@/hooks/useMatchMedia';
import ReasonDropdown from '@/components/ReasonDropdown';
import useSelfService from '@/modules/SelfServiceRegistration/hooks/useSelfService';

export default {
  components: {
    AdvisorDialog,
    BaseButton,
    ReasonDropdown,
    TypeBody,
  },
  props: {
    // For Heap reporting
    buttonId: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      default: "Let's see how we can help",
    },
    buttonTitle: {
      type: String,
      default: 'Next',
    },
    dropdownLabel: {
      type: String,
      default: 'What brought you to Bambee?',
    },
    // The price in this label differs per variant, so it is passed in rather
    // than hardcoded. Default is the control's, which matches what we sell.
    planLinkLabel: {
      type: String,
      default: 'Take me to the $9/mo plan',
    },
    // Makes the plan line a link (BIO-2206); without it the line stays plain text.
    planLinkHref: {
      type: String,
      default: '',
    },
  },
  setup(props) {
    const { state, updateRegistration } = useRegistration();
    const { isRadioPage } = useRadioPromo();
    const route = useRoute();
    const matchMedia = useMatchMedia();
    const isLarge = computed(() => matchMedia.value.sm || matchMedia.value.xs);
    const { goToNextPage } = useSelfService();

    const isInvalid = ref(false);

    const handleSubmit = () => {
      if (!state[RegistrationStateNames.WHAT_BROUGHT_YOU_TO_BAMBEE]) {
        isInvalid.value = true;

        return;
      }
      updateRegistration({
        [RegistrationStateNames.PAGE_REGISTERED_ON]: `${window.location.origin}${route.value.path}`,
        [RegistrationStateNames.FUNNEL]: selfCheckout,
      });
      Analytics.page({
        url: `${window.location.origin}/registration/${SelfServicePages[1].slug}`,
        path: `/registration/${SelfServicePages[1].slug}`,
        referrer: `${window.location.origin}${route.value.path}`,
      });
      goToNextPage();
    };

    const heading = computed(() => {
      return isRadioPage.value ? 'Schedule HR audit & overview' : props.title;
    });

    const buttonText = computed(() => props.buttonTitle);

    return {
      buttonText,
      handleSubmit,
      isInvalid,
      heading,
      isLarge,
    };
  },
};
</script>

<style scoped>
div >>> .field-label--large {
  @apply mb-8;
}

div >>> .select-input__selected {
  @apply max-h-8;
}
</style>
